"use client";

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { i18n } from '@/utils/i18n';
import { hasAdminAccess, isCustomerSupport } from '@/utils/rolePermissions';
import { UnifiedInbox } from '@/components/inbox/UnifiedInbox';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@vaiu/ui/card";
import { Button } from "@vaiu/ui/button";
import { Badge } from "@vaiu/ui/badge";
import { Separator } from "@vaiu/ui/separator";
import { Label } from "@vaiu/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@vaiu/ui/accordion";
import { 
  User, 
  Send, 
  Receipt, 
  History, 
  AlertCircle, 
  CheckCircle,
  QrCode,
  Plus,
  HelpCircle,
  LogOut,
  Users,
  MessageSquare,
  MoreHorizontal,
  Menu,
  ChevronUp,
  ChevronDown,
  BarChart3,
  Terminal,
  Settings,
  Gift,
  Code,
  HeadphonesIcon,
  ArrowRightLeft,
  ShoppingBag,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import CreateCashToken, { CreateTokenShell, type TokenMode } from './CreateCashToken';
import { Token } from './TokenCard';
import { platformApi } from '@/services/platformApi';
import { useToast } from '@/hooks/use-toast';
import { useTokenTheme } from '@/hooks/useTokenTheme';
import { useIsMobile } from '@/hooks/use-mobile';
import { onBalanceUpdate, emitTokenCreationBalanceUpdate, BalanceUpdateDetail } from '@/utils/lightEventEmitter';
// import VaiuLogo from './shared/VaiuLogo';
// import BrandLogo from './shared/BrandLogo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@vaiu/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@vaiu/ui/tooltip";
import { RedeemToken } from '@vaiu/redeem-ux';
import RecentActivity from './RecentActivity';
import Message from './Message';
import Dashboard from './Dashboard';
import AppTour from './tour/AppTour';
import CookieConsentBanner from './CookieConsentBanner';
import GuestUpgradePrompt from './GuestUpgradePrompt';
import GuestSessionIndicator from './GuestSessionIndicator';
import PayoutSetupReminder from './PayoutSetupReminder';
import MarketplaceSetupReminder from './MarketplaceSetupReminder';
import PaymentsParadigmGuide from '@/components/onboarding/PaymentsParadigmGuide';
import { resolveAuthUserId, withOwnMmMoneyBalance } from '@vaiu/utils/mmMoney';

const CorePageContent = () => {
  const { user, loading, error, logout, fetchProfile, isGuest } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { theme: tokenTheme } = useTokenTheme();
  const isMobile = useIsMobile();
  
  // Check if referrals feature is enabled via query parameter
  const searchParams = new URLSearchParams(location.search);
  const isReferralsEnabled = searchParams.get('referrals') === 'on';



  const [selectedCurrency, setSelectedCurrency] = useState(user?.currency || 'USD');
  const [latestToken, setLatestToken] = useState<Token | null>(null);
  const [createdToken, setCreatedToken] = useState<Token | null>(null);
  const [forceUpdate, setForceUpdate] = useState(0); // For language changes
  const isMerchantUser = !isGuest && user?.roles?.includes('MERCHANT');
  const [accordionValue, setAccordionValue] = useState<string | undefined>(
    isMerchantUser ? 'redeem-coin' : 'create-token'
  );
  const [createTokenMode, setCreateTokenMode] = useState<TokenMode>('give');

  useEffect(() => {
    setAccordionValue(isMerchantUser ? 'redeem-coin' : 'create-token');
  }, [isMerchantUser]);

  // Refs to access CreateCashToken's cleanup and reset functions
  const createCashTokenCleanupRef = useRef<(() => void) | null>(null);
  const createCashTokenResetRef = useRef<(() => void) | null>(null);
  
  // Local balance state that can be updated without full profile refresh
  const [localBalances, setLocalBalances] = useState(user?.balances || {});

  // Update local balances when user balances change
  useEffect(() => {
    if (user?.balances) {
      setLocalBalances(user.balances);
    }
  }, [user?.balances]);

  // No more isSubmitting or topup state needed - CreateCashToken handles it internally

  useEffect(() => {
    if (user?.currency) {
      setSelectedCurrency(user.currency);
    }
  }, [user?.currency]);

  // Authentication is now handled by ProtectedRoute wrapper
  // No need for individual auth checks here

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setForceUpdate(prev => prev + 1);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  // Listen for balance update events
  useEffect(() => {
    const handleBalanceUpdate = (detail: BalanceUpdateDetail) => {
      const { currency, amountChange, newBalance, reason, tokenKey } = detail;
      
      console.log('🔔 Balance update event received:', detail);
      
      setLocalBalances(prevBalances => ({
        ...prevBalances,
        [currency]: newBalance !== undefined ? newBalance : (prevBalances[currency] || 0) + amountChange
      }));
      
      // Show toast notification for balance change
      if (amountChange !== 0) {
        const changeText = amountChange > 0 ? `+${amountChange}` : `${amountChange}`;
        const reasonKey = reason === 'token_creation' ? 'core.toast.tokenCreatedTitle' : 
                          reason === 'token_redemption' ? 'core.toast.tokenRedeemedTitle' :
                          reason === 'topup' ? 'core.toast.balanceToppedUpTitle' : 'core.toast.balanceUpdatedTitle';
        const reasonText = i18n.__(reasonKey);
        const tokenKeyPart = tokenKey ? ` (${tokenKey})` : '';
        const description = i18n.__('core.toast.balanceChangedDesc', {
          currency,
          change: changeText,
          tokenKey: tokenKeyPart
        });
        
        toast({
          title: reasonText,
          description,
          duration: 3000,
        });
      }
    };

    // Use utility function to listen for balance update events
    const cleanup = onBalanceUpdate(handleBalanceUpdate);
    
    return cleanup;
  }, [toast]);

  // Handle PayPal return for non-token-creation flows (e.g., profile page top-up)
  // NOTE: Token creation PayPal returns are handled entirely by CreateCashToken component
  // This handler only processes profile top-ups and other non-token-creation PayPal returns
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paypalStatus = urlParams.get('paypal');
    const paypalContext = urlParams.get('context');
    
    // Skip topup contexts - CreateCashToken handles those completely
    if (paypalStatus === 'complete' && paypalContext && paypalContext.startsWith('topup:')) {
      // CreateCashToken handles all topup PayPal returns internally
      // No need to do anything here
      return;
    }
    
    // Handle non-token-creation PayPal returns (e.g., profile page top-up)
    if (paypalStatus === 'complete' && (!paypalContext || !paypalContext.startsWith('topup:'))) {
      console.log('💳 PayPal return for profile top-up (not token creation)');
      
      // Update local balances to reflect the top-up
      const existingToken = localStorage.getItem('authToken');
      if (existingToken) {
        fetchProfile(existingToken); // Refresh profile to get updated balances
      }
      
      toast({
        title: i18n.__('core.toast.paypalSuccess'),
        description: i18n.__('core.toast.balanceUpdated'),
        duration: 4000,
      });
      
      // Clean up URL parameters
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, [location.search, fetchProfile, toast]);

  // ProtectedRoute ensures user is authenticated, but we still handle loading/error states
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">{i18n.__('vaiu.core.loading')}</div>;
  }

  if (error) {
    return <div className="flex items-center justify-center min-h-screen">{i18n.__('vaiu.core.auth_error')} {error}</div>;
  }

  // User is guaranteed to exist due to ProtectedRoute wrapper

  const username = user.username || user.emails?.[0]?.address || 'User';

  // Debug the balances data
  console.log('🔍 Debug user balances:', {
    userBalances: user.balances,
    localBalances: localBalances,
    hasBalances: !!localBalances,
    balancesType: typeof localBalances,
    balancesKeys: localBalances ? Object.keys(localBalances) : 'none',
    balancesEntries: localBalances ? Object.entries(localBalances) : 'none'
  });

  // Use local balances with fallback test data if empty for debugging
  const displayBalances = withOwnMmMoneyBalance(
    localBalances && Object.keys(localBalances).length > 0
      ? localBalances
      : { EUR: 6.96, USD: 2.49 },
    resolveAuthUserId(user)
  );

  console.log('🔍 Using display balances:', displayBalances);


  
  const handleReferralTokens = () => {
    console.log('Referral Tokens clicked');
    // Navigate to referral tokens page
    navigate('/referral-tokens');
  };

  // Enhanced token creation handler with balance update
  const handleTokenCreated = async (token: Token) => {
    console.log('✅ Token created by CreateCashToken:', token);
    
    setLatestToken(token);
    setCreatedToken(token);
    
    // Keep the accordion open on the create-token section so the token replaces the form in place
    setAccordionValue(token.type === 'invoice' ? 'create-invoice' : 'create-token');
    
    // CRITICAL FIX: Fetch the actual balance from the backend
    // The backend should have deducted the balance when creating the token
    // We must verify this actually happened to prevent infinite money glitch
    
    // Don't update profile immediately - it causes UI flashing
    // Instead, just update the local balance state after a delay
    setTimeout(async () => {
      try {
        console.log('🔄 Fetching updated balance from backend...');
        const currentToken = localStorage.getItem('authToken');
        if (currentToken) {
          // Just fetch the balance, not the entire profile
          const balanceData = await platformApi.getBalance(selectedCurrency);
          if (balanceData && typeof balanceData.balance === 'number') {
            console.log('✅ Balance updated:', balanceData.balance);
            setLocalBalances(prev => ({
              ...prev,
              [selectedCurrency]: balanceData.balance
            }));
          }
        }
      } catch (error) {
        // Log the error but don't bother the user
        console.error('⚠️ Could not refresh balance:', error);
        console.log('💡 Balance will update on next page refresh');
      }
    }, 1000); // 1 second delay to let backend settle
    
    // REMOVED: Optimistic frontend-only balance update that was causing the infinite money glitch
    // The balance should only be updated from the backend's authoritative source
  };

  const handleDismissCreatedToken = () => {
    setCreatedToken(null);
    setLatestToken(null);

    if (createCashTokenCleanupRef.current) {
      console.log('🧹 [CorePageContent] Triggering PayPal cleanup after token card closed');
      createCashTokenCleanupRef.current();
    }

    if (createCashTokenResetRef.current) {
      console.log('🔄 [CorePageContent] Resetting token creation state to allow new token');
      createCashTokenResetRef.current();
    }
  };

  // No more topup handlers needed - CreateCashToken handles everything internally

  return (
    <div className="w-full space-y-4">
      {/* Guest Session Indicator - Show session status for guest users */}
      {isGuest && (
        <>
          {/* <GuestSessionIndicator className="mb-4" /> */}
          <GuestUpgradePrompt 
            variant="banner" 
            showSessionExpiry={false}
          />
        </>
      )}
      
      {/* Unified Inbox - Collapsed by default, expands if there are messages */}
      <div className="mb-6">
        <UnifiedInbox collapsed={true} />
      </div>
      
      {/* Cookie Consent */}
      <Card className="bg-white shadow-sm border">
        <CardContent className="p-0">
          {/* Cookie Consent - Not shown for guests */}
          {!isGuest && <CookieConsentBanner key="cookie-consent" />}
        </CardContent>
      </Card>

      {/* Tour Component - Below cookie consent - Not shown for guests */}
      {!isGuest && <AppTour key="app-tour" />}

      {/* Payout Setup Reminder - For merchants without payout methods */}
      {!isGuest && user?.roles?.includes('MERCHANT') && (
        <PayoutSetupReminder key="payout-setup-reminder" />
      )}

      {/* Marketplace Setup Reminder - For merchants without complete catalog */}
      {!isGuest && user?.roles?.includes('MERCHANT') && (
        <MarketplaceSetupReminder key="marketplace-setup-reminder" />
      )}

      {/* Collapsible Dashboard Section */}
      <Card>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="dashboard" className="border-none">
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center space-x-2">
                  <BarChart3 className="w-5 h-5" />
                  <span className="text-lg font-semibold">{i18n.__('vaiu.core.dashboard')}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4">
                <Dashboard />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
      </Card>

      <PaymentsParadigmGuide
        forceHidden={searchParams.get('embed') === 'true'}
        onSenderPath={() => {
          setAccordionValue('create-token');
          requestAnimationFrame(() => {
            document
              .getElementById('vaiu-main-pay-accordion')
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        }}
        onReceiverPath={() => {
          setAccordionValue('redeem-coin');
          requestAnimationFrame(() => {
            document
              .getElementById('vaiu-main-pay-accordion')
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        }}
      />

      {/* Main Actions Accordion */}
      <Card id="vaiu-main-pay-accordion">
          <CardContent className="p-0">
            <Accordion 
              type="single" 
              collapsible 
              value={accordionValue}
              onValueChange={setAccordionValue}
              className="w-full"
            >
          {/* Unified Create Token Section with Slider */}
          <AccordionItem value="create-token">
            <CreateTokenShell
              recentToken={createdToken}
              onRecentTokenDismiss={handleDismissCreatedToken}
              tokenMode={createTokenMode}
              onTokenModeChange={setCreateTokenMode}
              theme={tokenTheme}
            >
              <CreateCashToken 
                typeOfToken="cash"
                selectedCurrency={selectedCurrency}
                balances={displayBalances}
                tokenTheme={tokenTheme}
                onSubmit={handleTokenCreated}
                showTypeSlider={true}
                tokenMode={createTokenMode}
                onTokenModeChange={setCreateTokenMode}
                hideTypeSelector={true}
                isPersonalMode={true}
                showTokenAfterCreation={false}
                cleanupPayPalParamsRef={createCashTokenCleanupRef}
                resetTokenCreationRef={createCashTokenResetRef}
              />
            </CreateTokenShell>
          </AccordionItem>

          {/* Redeem Coin Section */}
          {!user.getSectionDisabled && (
            <AccordionItem value="redeem-coin">
              <AccordionTrigger className="text-left hover:no-underline px-6 py-4">
                <div className="flex items-center gap-2">
                  <Gift className="w-5 h-5" />
                  <span className="text-lg font-semibold">{i18n.__('vaiu.core.redeem_token')}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="px-6 pb-4">
                  <RedeemToken key="redeem-token-single-instance" />
                </div>
              </AccordionContent>
            </AccordionItem>
          )}
            </Accordion>
          </CardContent>
      </Card>

      {/* Recent Activity */}
      <RecentActivity />

      {/* KYC/Funding Sources */}
      <div className="space-y-4">
          {/* Referral Tokens Button - Only show if referrals=on query param is present */}
          {isReferralsEnabled && (
            <Button 
              onClick={handleReferralTokens}
              className="w-full flex items-center justify-center space-x-2 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
              size="lg"
            >
              <Gift className="w-5 h-5" />
              <span>{i18n.__('core.referralTokens')}</span>
            </Button>
          )}
      </div>

      {/* Message Component */}
      <Message />
    </div>
  );
};

export default CorePageContent;
